const EXPORT_FORMAT = "moo-sync-catalog";
const EXPORT_VERSION = 1;

const asNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const readVariationExtraRules = (variation) => {
  if (Array.isArray(variation?.extras_config)) return variation.extras_config;
  if (typeof variation?.extras_config !== "string") return [];
  try {
    const parsed = JSON.parse(variation.extras_config);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const extraIdentity = (extra) => ({
  name: String(extra?.name || "").trim(),
  category: String(extra?.category || "Général").trim() || "Général",
});

export const buildProductCatalogExport = ({ products = [], categories = [], extras = [] } = {}) => {
  const activeProducts = products.filter((product) => Number(product?.is_active ?? 1) === 1);
  const activeExtras = extras.filter((extra) => Number(extra?.is_active ?? 1) === 1);
  const extrasById = new Map(activeExtras.map((extra) => [Number(extra.id), extra]));
  const categoriesById = new Map(categories.map((category) => [Number(category.id), category]));

  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    categories: categories
      .filter((category) => Number(category?.is_active ?? 1) === 1)
      .map((category) => ({
        name: category.name,
        description: category.description || "",
        color: category.color || null,
        icon: category.icon || null,
        sort_order: asNumberOrNull(category.sort_order) ?? 0,
      })),
    extras: activeExtras.map((extra) => ({
      ...extraIdentity(extra),
      description: extra.description || "",
      base_price: asNumberOrNull(extra.base_price) ?? 0,
      sort_order: asNumberOrNull(extra.sort_order) ?? 0,
      track_stock: Boolean(extra.track_stock),
      inventory_kind: extra.inventory_kind || null,
    })),
    products: activeProducts.map((product) => {
      const productRules = (product.extras || []).map((rule) => ({
        extra: extraIdentity(rule),
        type: rule.type || "supplement",
        price: asNumberOrNull(rule.price) ?? asNumberOrNull(rule.base_price) ?? 0,
        max_quantity: Math.max(1, Number(rule.max_quantity) || 1),
        is_default: Boolean(rule.is_default),
      }));
      const variations = (product.variations || [])
        .filter((variation) => Number(variation?.is_active ?? 1) === 1)
        .map((variation) => ({
          name: variation.name || "",
          sku: variation.sku || null,
          attributes: variation.attributes || {},
          price: asNumberOrNull(variation.price),
          cost_price: asNumberOrNull(variation.cost_price) ?? 0,
          yango_price: asNumberOrNull(variation.yango_price),
          glovo_price: asNumberOrNull(variation.glovo_price),
          sort_order: asNumberOrNull(variation.sort_order) ?? 0,
          track_stock: Boolean(variation.track_stock),
          stock_quantity: asNumberOrNull(variation.stock_quantity) ?? 0,
          free_accompaniment_quota: asNumberOrNull(variation.free_accompaniment_quota),
          daily_menu_active: Boolean(variation.daily_menu_active),
          daily_menu_price: asNumberOrNull(variation.daily_menu_price),
          daily_menu_date: variation.daily_menu_date || null,
          extras: readVariationExtraRules(variation)
            .map((rule) => {
              const extra = extrasById.get(Number(rule?.id ?? rule?.extra_id));
              if (!extra) return null;
              return {
                extra: extraIdentity(extra),
                type: rule.type || "supplement",
                price: asNumberOrNull(rule.price) ?? asNumberOrNull(extra.base_price) ?? 0,
                max_quantity: Math.max(1, Number(rule.max_quantity) || 1),
                is_default: Boolean(rule.is_default),
              };
            })
            .filter(Boolean),
        }));
      return {
        name: product.name,
        sku: product.sku || null,
        description: product.description || "",
        category: categoriesById.get(Number(product.category_id))?.name || product.category_name || "",
        price: asNumberOrNull(product.price),
        cost_price: asNumberOrNull(product.cost_price) ?? 0,
        yango_price: asNumberOrNull(product.yango_price),
        glovo_price: asNumberOrNull(product.glovo_price),
        destination: product.destination || "cuisine",
        preparation_time: asNumberOrNull(product.preparation_time) ?? 0,
        image_url: product.image_url || null,
        image_path: product.image_path || null,
        is_available: Boolean(product.is_available),
        product_type: product.product_type || (variations.length ? "variable" : "simple"),
        track_stock: Boolean(product.track_stock),
        inventory_kind: product.inventory_kind || null,
        is_recipe_ingredient: Boolean(product.is_recipe_ingredient),
        unit_type: product.unit_type || "Unité",
        has_accompaniments: Boolean(product.has_accompaniments),
        has_supplements: Boolean(product.has_supplements),
        free_accompaniment_quota: Math.max(0, Number(product.free_accompaniment_quota ?? 1) || 0),
        daily_menu_active: Boolean(product.daily_menu_active),
        daily_menu_scope: product.daily_menu_scope || "global",
        daily_menu_price: asNumberOrNull(product.daily_menu_price),
        daily_menu_date: product.daily_menu_date || null,
        extras: productRules,
        variations,
      };
    }),
  };
};

export const isProductCatalogExport = (value) =>
  value?.format === EXPORT_FORMAT &&
  Number(value?.version) === EXPORT_VERSION &&
  Array.isArray(value?.products);

const normalizeIdentity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const buildProductCatalogImportPreview = ({
  incomingProducts = [],
  existingProducts = [],
  categories = [],
} = {}) => {
  const categoryNamesById = new Map(
    categories.map((category) => [Number(category.id), String(category.name || "").trim()]),
  );
  const existingBySku = new Map();
  const existingByCategoryName = new Map();

  existingProducts.forEach((product) => {
    const sku = normalizeIdentity(product?.sku);
    if (sku) existingBySku.set(sku, product);
    const category = normalizeIdentity(
      product?.category_name || categoryNamesById.get(Number(product?.category_id)),
    );
    const name = normalizeIdentity(product?.name);
    if (category && name) existingByCategoryName.set(`${category}:${name}`, product);
  });

  const uniqueIncoming = new Map();
  incomingProducts.forEach((product) => {
    const sku = normalizeIdentity(product?.sku);
    const category = normalizeIdentity(product?.category || product?.category_name);
    const name = normalizeIdentity(product?.name);
    if (!name) return;
    const key = sku ? `sku:${sku}` : `name:${category}:${name}`;
    if (!uniqueIncoming.has(key)) uniqueIncoming.set(key, product);
  });

  const summary = { total: uniqueIncoming.size, create: 0, update: 0, reactivate: 0, withoutSku: 0 };
  uniqueIncoming.forEach((product) => {
    const sku = normalizeIdentity(product?.sku);
    const category = normalizeIdentity(product?.category || product?.category_name);
    const name = normalizeIdentity(product?.name);
    if (!sku) summary.withoutSku += 1;
    const existing = (sku && existingBySku.get(sku)) ||
      existingByCategoryName.get(`${category}:${name}`);
    if (!existing) summary.create += 1;
    else if (Number(existing.is_active ?? 1) === 0) summary.reactivate += 1;
    else summary.update += 1;
  });

  return summary;
};
